import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { ref, computed, watch, onBeforeUnmount, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { Search, Loader2 as Loader, ChevronRight } from 'lucide-vue-next';
import { PopoverRoot, PopoverAnchor } from 'reka-ui';
import { PopoverContent } from '../../primitives/popover';
import { Kbd as KbdShadcn } from 'Components/primitives/kbd';
import EnDashboardIconWrapper from 'Components/icons/EnDashboardIconWrapper.vue';
import ModuleInfo from 'Components/types/ModuleInfo';
import { SubgroupIcons } from 'Components/navigation/utils/navigationGroups';
import { EnDashboardIconVariants } from 'Components/icons/EnDashboardIconVariants';
import { useRecentlyViewed } from 'Components/navigation/utils/useRecentlyViewed';
import { useAccountNavigation } from 'Components/navigation/utils/useAccountNavigation';
import { NavigationIcons } from 'Components/icons/navigationIcons';
import { enFetch } from 'Js/utils/fetchUtils';
import { useFocusHotkey } from '../composables/useFocusHotkey';
export default /*@__PURE__*/ _defineComponent({
    __name: 'EnNavbarSearch',
    props: /*@__PURE__*/ _mergeModels({
        engineUrl: { type: String, required: false },
        isAuthUrl: { type: Boolean, required: false }
    }, {
        "modelValue": { type: String, ...{ default: '' } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["update:modelValue", "select"], ["update:modelValue"]),
    setup(__props, { expose: __expose, emit: __emit }) {
        __expose();
        const props = __props;
        const emit = __emit;
        const { t } = useI18n();
        const search = _useModel(__props, 'modelValue');
        const menu = ref(false);
        const highlighted = ref(-1);
        const loading = ref(false);
        const searchResults = ref([]);
        const activeListType = ref('search');
        let debounceTimeout = null;
        const inputRef = useTemplateRef('inputRef');
        const anchorRef = useTemplateRef('anchorRef');
        /**
         * The popover is anchored to (not triggered by) the field, so reka would treat
         * focusing/clicking the input as an outside interaction and close immediately.
         * Keep it open while the interaction is within our own field.
         */
        function onOutsideInteraction(event) {
            const target = event.detail.originalEvent.target;
            if (target && anchorRef.value?.contains(target)) {
                event.preventDefault();
            }
        }
        const { recentlyViewed } = useRecentlyViewed();
        const recentItems = computed(() => (recentlyViewed.value ?? []).slice(0, 10));
        const currentEnpowerAccount = computed(() => localStorage.getItem('enPowerAccountId') || '');
        const { buildUrl, navigateTo: goToUrl } = useAccountNavigation(undefined, props.engineUrl);
        /** The list the keyboard navigation currently acts on. */
        const activeList = computed(() => activeListType.value === 'search' ? searchResults.value : recentItems.value);
        function getAreaIcon(area) {
            return SubgroupIcons[area];
        }
        function getModuleIcon(module) {
            return module.icon || NavigationIcons.Other;
        }
        watch(search, (value) => {
            emit('update:modelValue', value ?? '');
            highlighted.value = -1;
            if (debounceTimeout)
                clearTimeout(debounceTimeout);
            if (value?.trim()) {
                debounceTimeout = setTimeout(() => fetchModuleConfigurations(value), 300);
            }
            else {
                searchResults.value = [];
                highlighted.value = -1;
            }
        });
        watch(menu, (open) => {
            if (!open) {
                highlighted.value = -1;
                return;
            }
            if (searchResults.value.length > 0) {
                activeListType.value = 'search';
                highlighted.value = 0;
            }
            else if (!search.value.trim() && recentItems.value.length > 0) {
                activeListType.value = 'recent';
                highlighted.value = 0;
            }
            else {
                highlighted.value = -1;
            }
        });
        async function fetchModules(searchQuery) {
            const url = props.isAuthUrl
                ? '/api/Configuration/GetModuleConfigurations'
                : '/api/ModuleConfig/GetModuleConfigurations';
            const response = await enFetch({ url, getParams: { name: searchQuery } });
            if (!response.ok) {
                console.error('Fetch error:', await response.text());
                return [];
            }
            const data = await response.json();
            return data.slice(0, 10).map((module) => new ModuleInfo({
                area: module.Area,
                icon: getAreaIcon(module.Area),
                name: module.Name,
                url: module.Url,
            }));
        }
        async function fetchModuleConfigurations(searchQuery) {
            if (!searchQuery.trim())
                return;
            loading.value = true;
            try {
                searchResults.value = await fetchModules(searchQuery);
                if (searchResults.value.length > 0) {
                    highlighted.value = 0;
                    activeListType.value = 'search';
                }
            }
            catch (error) {
                console.error('Fetch exception:', error);
                searchResults.value = [];
            }
            finally {
                loading.value = false;
            }
        }
        function navigateTo(module) {
            if (!module)
                return;
            emit('select', module);
            if (module.url)
                goToUrl(buildUrl(module.url, currentEnpowerAccount.value));
        }
        function onArrowDown() {
            const count = activeList.value.length;
            if (!count)
                return;
            menu.value = true;
            // Loop: last -> first.
            highlighted.value = (highlighted.value + 1) % count;
        }
        function onArrowUp() {
            const count = activeList.value.length;
            if (!count)
                return;
            // Loop: first -> last.
            highlighted.value = (highlighted.value - 1 + count) % count;
        }
        function onEnter() {
            if (highlighted.value < 0)
                return;
            navigateTo(activeList.value[highlighted.value]);
        }
        /** Highlight the hovered row and switch the active list to match. */
        function highlightItem(index, listType) {
            highlighted.value = index;
            activeListType.value = listType;
        }
        function highlightText(text, searchTerm) {
            if (!searchTerm || !text)
                return text ?? '';
            const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(`(${escapedTerm})`, 'gi');
            return text.replace(regex, '<mark>$1</mark>');
        }
        // Global "/" shortcut: focus the search when nothing else is focused.
        useFocusHotkey('/', inputRef, () => (menu.value = true));
        onBeforeUnmount(() => {
            if (debounceTimeout)
                clearTimeout(debounceTimeout);
        });
        const __returned__ = { props, emit, t, search, menu, highlighted, loading, searchResults, activeListType, get debounceTimeout() { return debounceTimeout; }, set debounceTimeout(v) { debounceTimeout = v; }, inputRef, anchorRef, onOutsideInteraction, recentlyViewed, recentItems, currentEnpowerAccount, buildUrl, goToUrl, activeList, getAreaIcon, getModuleIcon, fetchModules, fetchModuleConfigurations, navigateTo, onArrowDown, onArrowUp, onEnter, highlightItem, highlightText, get Search() { return Search; }, get Loader() { return Loader; }, get ChevronRight() { return ChevronRight; }, get PopoverRoot() { return PopoverRoot; }, get PopoverAnchor() { return PopoverAnchor; }, get PopoverContent() { return PopoverContent; }, get KbdShadcn() { return KbdShadcn; }, EnDashboardIconWrapper, get EnDashboardIconVariants() { return EnDashboardIconVariants; } };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
